import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '@lingui/macro';
import isEmpty from 'lodash/isEmpty';
import { Breadcrumbs, Button, Grid } from '@decisiv/ui-components';
import PageTitle from '../../components/PageTitle/PageTitle';
import PaddedForm from '../../components/PaddedForm';
import AsteriskSubtitle from '../../components/AsteriskSubtitle';
import CommandsApi from '../../api/commands';
import ServiceNetworkApi from '../../api/service_network';
import CategoriesApi from '../../api/categories';
import ActionsApi from '../../api/actions';
import ApplicationEventsApi from '../../api/application_events';
import CreateModal from './create_modal';
import BasicInformation from './basic_information';
import Scope from './scope';
import Trigger from './trigger';
import Commands from './commands';
import Metadata from './metadata';
import ActionMetadataNotice from '../../components/ActionMetadataNotice';
import { seedDeclaredMetadata } from '../../utils/actionMetadataSchema';
import CacheSettings from './cache_settings';
import CircuitBreaker from './circuit_breaker';
import AutomaticRetry from './automatic_retry';
import AutoEnable from './auto_enable';
import authorizeOn from '../../utils/resourceAuthorizer';
import Helmet from '../../components/SEO/Helmet';

export default function IntegrationCreatePage() {
  const navigate = useNavigate();
  const formDefault = {
    name: null,
    description: null,
    audience: [],
    service_network: [],
    events: [],
    trigger_type: [],
    http_api_request_url: null,
    commands_flow: [''],
    action: null,
    action_uuid: null,
    category: null,
    cache_enabled: false,
    cache_ttl: 0,
    circuit_breaker_enabled: false,
    circuit_breaker_retry_after: 0,
    circuit_breaker_failure_threshold: 0,
    automatic_retry_enabled: false,
    retry_after: 0,
    max_retries: 0,
    auto_enable: false,
    metadata: [],
  };

  const [integration, setIntegration] = useState({});
  const TRIGGER_TYPE_APPLICATION_EVENT = 'application_event';
  const TRIGGER_TYPE_HTTP_API_REQUEST = 'http_api_request';

  const [commands, setCommands] = useState([]);
  const [serviceNetworkItems, setServiceNetworkItems] = useState([]);
  const [applicationEventsItems, setApplicationEventsItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [createBtnDisabledStatus, setCreateBtnDisabledStatus] = useState(true);
  const [triggerTypeApplicationEvent, setTriggerTypeApplicationEvent] =
    useState(false);
  const [showAutomaticRetryFields, setShowAutomaticRetryFields] =
    useState(false);
  const [showCircuitBreakerFields, setShowCircuitBreakerFields] =
    useState(false);
  const [triggerTypeHttpRequest, setTriggerTypeHttpRequest] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState(formDefault);
  const [commandsFlow, setCommandsFlow] = useState(['']);
  const [metadataInputs, setMetadataInputs] = useState([]);
  const [enabledAddMetadataInput, setEnabledAddMetadataInput] = useState(true);
  // PD-2651: switched from the legacy `/categories-and-actions` endpoint to
  // the DB-backed `/categories` + `/actions` pair. `allActions` holds every
  // unlocked, latest-in-chain Action; we filter it by `category_uuid` when
  // the user picks a category, mirroring the old two-step UX.
  const [allActions, setAllActions] = useState([]);
  const [categoryActions, setCategoryActions] = useState([]);

  function parseCommands({ data }) {
    const parsedData = data.map((command) => ({
      id: command.attributes.name,
      connections: command.attributes.connections,
    }));

    return parsedData;
  }

  // Category dropdown items. New endpoint returns JSON:API rows with a
  // UUID `id` and `attributes.name`. We keep the dropdown's id/value as
  // the UUID (used downstream to filter actions by category_uuid) but
  // expose the name as the label/visible string.
  function parseCategories({ data }) {
    return data.map((category) => ({
      id: category.id,
      label: category.attributes.name,
      value: category.id,
      name: category.attributes.name,
    }));
  }

  // Action dropdown items. `id`/`value` are the Action UUID (so the
  // handler can resolve it back to a full record), `label`/`name` keep
  // the snake_case Action name for muscle-memory. Switch to `alias_name`
  // here later if product wants friendlier labels.
  function parseActions(actions) {
    return actions.map((action) => ({
      id: action.id,
      label: action.attributes.name,
      value: action.id,
      name: action.attributes.name,
    }));
  }

  useEffect(() => {
    async function getData() {
      if (!authorizeOn()) {
        navigate('/integrations');
        return;
      }

      setCategories([
        {
          id: 'loading',
          label: t`Loading...`,
          value: 'loading',
          disabled: true,
        },
      ]);

      setServiceNetworkItems([
        {
          id: 'loading',
          label: t`Loading...`,
          value: 'loading',
          disabled: true,
        },
      ]);

      setApplicationEventsItems([
        {
          id: 'loading',
          label: t`Loading...`,
          value: 'loading',
          disabled: true,
        },
      ]);

      setCommands([
        {
          id: 'loading',
          label: t`Loading...`,
          value: 'loading',
          disabled: true,
        },
      ]);

      setCategoryActions([
        {
          id: 'loading',
          label: t`Loading...`,
          value: 'loading',
          disabled: true,
        },
      ]);

      // PD-2651: two parallel fetches replace the legacy
      // `/categories-and-actions` call. Categories drive the first dropdown;
      // actions are pre-loaded so the second dropdown can filter without an
      // extra round-trip when the user picks a category. `latest: true`
      // hides superseded versions; `locked: false` hides actions removed
      // from new-assignment eligibility.
      const [responseCategories, responseActions] = await Promise.all([
        CategoriesApi.listCategories(),
        ActionsApi.fetchActionsList({
          locked: false,
          latest: true,
          limit: 200,
        }),
      ]);

      /* istanbul ignore else */
      if (responseCategories.data) {
        setCategories(parseCategories(responseCategories));
      }

      /* istanbul ignore else */
      if (responseActions.data) {
        setAllActions(responseActions.data);
      }

      const responseCommands = await CommandsApi.fetchCommands();
      /* istanbul ignore else */
      if (responseCommands.data) {
        const parsedCommands = parseCommands(responseCommands);
        setCommands(parsedCommands);
      }

      const responseServiceNetwork =
        await ServiceNetworkApi.fetchServiceNetwork();
      /* istanbul ignore else */
      if (responseServiceNetwork.data) {
        const parsedServiceNetworks = responseServiceNetwork.data.map((sn) => ({
          id: sn.attributes.name,
          label: sn.attributes.name,
          value: sn.attributes.name,
        }));
        setServiceNetworkItems(parsedServiceNetworks);
      }

      const responseApplicationEvents =
        await ApplicationEventsApi.fetchApplicationEvents();
      /* istanbul ignore else */
      if (responseApplicationEvents.data) {
        const parsedApplicationEvents = responseApplicationEvents.data.map(
          (sn) => ({
            id: sn.attributes.name,
            label: sn.attributes.name,
            value: sn.attributes.name,
          }),
        );
        setApplicationEventsItems(parsedApplicationEvents);
      }
    }

    getData();
  }, []);

  // Refresh just the picker's reference data (categories + actions) on focus /
  // bfcache restore — not the main `getData`, which would clobber form edits.
  useEffect(() => {
    const refreshPickerData = async () => {
      const [responseCategories, responseActions] = await Promise.all([
        CategoriesApi.listCategories(),
        ActionsApi.fetchActionsList({
          locked: false,
          latest: true,
          limit: 200,
        }),
      ]);
      if (responseCategories.data) {
        setCategories(parseCategories(responseCategories));
      }
      if (responseActions.data) {
        setAllActions(responseActions.data);
      }
    };

    const onFocus = () => refreshPickerData();
    const onPageShow = (e) => {
      if (e.persisted) refreshPickerData();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  const handleToggleCreateModal = () => setShowCreateModal(!showCreateModal);

  const handleForm = ({ event, selectedId, options, field, customValues }) => {
    const index = isEmpty(field) ? event.target.name : field;
    const newForm = form;
    let cacheTTLChanged = false;

    switch (index) {
      // managing array value fields
      case 'audience':
      case 'trigger_type': {
        const eventName = event.target.value;

        if (event.target.checked) {
          if (eventName === TRIGGER_TYPE_APPLICATION_EVENT) {
            newForm.automatic_retry_enabled = true;
            setShowAutomaticRetryFields(true);
            newForm.retry_after = 60;
            newForm.max_retries = 5;
            newForm.circuit_breaker_enabled = true;
            setShowCircuitBreakerFields(true);
            newForm.circuit_breaker_retry_after = 60;
            newForm.circuit_breaker_failure_threshold = 5;
          }
          if (eventName === TRIGGER_TYPE_HTTP_API_REQUEST) {
            newForm.circuit_breaker_enabled = true;
            setShowCircuitBreakerFields(true);
            newForm.circuit_breaker_retry_after = 60;
            newForm.circuit_breaker_failure_threshold = 5;
          }
          newForm[index].push(eventName);
        } else {
          const itemIndex = newForm[index].indexOf(event.target.value);
          if (itemIndex !== -1) {
            newForm[index].splice(itemIndex, 1);
          }
          if (eventName === TRIGGER_TYPE_APPLICATION_EVENT) {
            newForm.automatic_retry_enabled = false;
            setShowAutomaticRetryFields(false);
            newForm.retry_after = 0;
            newForm.max_retries = 0;
          }
          if (newForm.trigger_type.length === 0) {
            newForm.circuit_breaker_enabled = false;
            setShowCircuitBreakerFields(false);
            newForm.circuit_breaker_retry_after = 0;
            newForm.circuit_breaker_failure_threshold = 0;
          }
        }
        break;
      }
      case 'service_network':
      case 'events':
        // managing combobox multiple values fields
        if (
          !newForm[index].includes(selectedId) &&
          !isEmpty(selectedId) &&
          !options.removed
        ) {
          newForm[index].push(selectedId);
        }

        /* istanbul ignore next */
        if (newForm[index].includes(selectedId) && options.removed) {
          const indexToRemove = newForm[index].indexOf(selectedId);
          if (indexToRemove !== -1) {
            newForm[index].splice(indexToRemove, 1);
          }
        }

        if (isEmpty(integration)) {
          integration.attributes = {};
          integration.attributes.service_network = [];
          integration.attributes.events = [];
        }
        integration.attributes[index] = newForm[index];
        setIntegration(integration);

        break;
      case 'custom_events':
        newForm.events = customValues;
        if (isEmpty(integration)) {
          integration.attributes = {};
          integration.attributes.service_network = [];
          integration.attributes.events = [];
        }
        integration.attributes.events = newForm.events;
        setIntegration(integration);

        break;
      case 'commands_flow':
        newForm[index] = event;
        setCommandsFlow(event);
        break;
      case 'action': {
        // selectedId is the Action UUID. Resolve it to a full record so we
        // can populate both columns the backend persists:
        //   - `action` (string, legacy back-compat) — Action.name
        //   - `action_uuid` (FK, new source of truth)
        const selectedAction = allActions.find((a) => a.id === selectedId);
        newForm.action_uuid = selectedId || null;
        newForm.action = selectedAction?.attributes?.name || null;
        // Pre-seed the metadata keys the action declares via metadata_schema
        // so they're ready to fill in the metadata editor.
        const seededMetadata = seedDeclaredMetadata(
          newForm.metadata,
          selectedAction?.attributes?.metadata_schema,
        );
        if (seededMetadata !== newForm.metadata) {
          newForm.metadata = seededMetadata;
          setMetadataInputs(seededMetadata);
        }
        break;
      }
      case 'auto_enable':
        newForm[index] = event.target.value === 'true';
        break;
      case 'cache_enabled':
        newForm[index] = event.target.value === 'true';

        if (event.target.value === 'false') {
          newForm.cache_ttl = 0;
        } else if (newForm.cache_ttl === 0) {
          newForm.cache_ttl = 300;
        }
        break;
      case 'cache_ttl': {
        const value = event.target.value.replace(/\D/g, '');
        newForm[index] = value;
        cacheTTLChanged = true;
        setErrors({ ...errors, cache_ttl: '' });
        break;
      }
      case 'circuit_breaker_enabled':
        newForm[index] = event.target.value === true;

        if (event.target.value === false) {
          setShowCircuitBreakerFields(false);
          newForm.circuit_breaker_retry_after = 0;
          newForm.circuit_breaker_failure_threshold = 0;
        }
        if (event.target.value === true) {
          setShowCircuitBreakerFields(true);
          newForm.circuit_breaker_retry_after = 60;
          newForm.circuit_breaker_failure_threshold = 5;
        }
        break;
      case 'circuit_breaker_retry_after': {
        const cbRetryAfter = event.target.value.replace(/\D/g, '');
        newForm[index] = cbRetryAfter;
        break;
      }
      case 'circuit_breaker_failure_threshold': {
        const cbMaxRetryValue = event.target.value.replace(/\D/g, '');
        newForm[index] = cbMaxRetryValue;
        break;
      }
      case 'automatic_retry_enabled':
        newForm[index] = event.target.value === true;

        if (event.target.value === false) {
          newForm.retry_after = 0;
          newForm.max_retries = 0;
          setShowAutomaticRetryFields(false);
        }
        if (event.target.value === true) {
          setShowAutomaticRetryFields(true);
          newForm.retry_after = 60;
          newForm.max_retries = 5;
        }
        break;
      case 'retry_after': {
        let retryValue = event.target.value.replace(/\D/g, '');
        if (retryValue > 900) {
          retryValue = 900;
        }
        newForm[index] = retryValue;
        break;
      }
      case 'max_retries': {
        const maxRetryValue = event.target.value.replace(/\D/g, '');
        newForm[index] = maxRetryValue;
        break;
      }
      case 'metadata': {
        const myIndex = parseInt(event.target.name.slice(-1), 10);
        const fieldName = event.target.name.slice(0, -2);

        newForm[index][myIndex][fieldName] = event.target.value;
        setForm(newForm);
        setMetadataInputs([...newForm.metadata]);

        setEnabledAddMetadataInput(
          newForm[index].every((element) => element.key !== ''),
        );

        break;
      }
      case 'category': {
        // selectedId is the Category UUID coming from the dropdown. The
        // backend's `integrations.category` column is still the category
        // name string (no FK), so we look up the name from `categories`
        // and persist that. The UUID stays in scope only long enough to
        // filter `allActions` for the second dropdown.
        if (!isEmpty(selectedId)) {
          const selectedCategory = categories.find((c) => c.id === selectedId);
          newForm.category = selectedCategory?.name || null;
          const matching = allActions.filter(
            (a) => a.attributes?.category_uuid === selectedId,
          );
          setCategoryActions(parseActions(matching));
        } else {
          newForm.category = null;
          setCategoryActions([]);
        }
        // Switching category invalidates any previously-picked action.
        newForm.action = null;
        newForm.action_uuid = null;
        break;
      }
      default:
        // managing text value fields
        /* istanbul ignore next */
        newForm[index] = event.target.value.includes('{')
          ? event.target.value.replace(/\n/g, '')
          : event.target.value;
    }

    setForm(newForm);

    /* istanbul ignore next */
    const btnStatus =
      isEmpty(newForm.category) ||
      isEmpty(newForm.name) ||
      isEmpty(newForm.audience) ||
      isEmpty(newForm.trigger_type) ||
      isEmpty(newForm.description.replace(/\s/g, '')) ||
      newForm.commands_flow.includes(undefined) ||
      newForm.commands_flow.includes('') ||
      (newForm.trigger_type.includes(TRIGGER_TYPE_APPLICATION_EVENT) &&
        isEmpty(newForm.events)) ||
      (newForm.trigger_type.includes(TRIGGER_TYPE_HTTP_API_REQUEST) &&
        isEmpty(newForm.action)) ||
      (!cacheTTLChanged && !isEmpty(errors.cache_ttl));
    setCreateBtnDisabledStatus(btnStatus);

    setTriggerTypeApplicationEvent(
      newForm.trigger_type.includes(TRIGGER_TYPE_APPLICATION_EVENT),
    );
    setTriggerTypeHttpRequest(
      newForm.trigger_type.includes(TRIGGER_TYPE_HTTP_API_REQUEST),
    );
  };

  return (
    <>
      <Helmet title={t`Create Integration`} />
      <CreateModal
        handleToggleCreateModal={handleToggleCreateModal}
        showCreateModal={showCreateModal}
        form={form}
        triggerTypeHttpRequest={triggerTypeHttpRequest}
        triggerTypeApplicationEvent={triggerTypeApplicationEvent}
      />

      <Breadcrumbs
        path={[{ to: '/integrations', text: 'Integrations' }]}
        current={t`Create Integration`}
        style={{ padding: '0.375rem 1.3125rem' }}
      />
      <PageTitle
        title={t`Create Integration`}
        subtitle={<AsteriskSubtitle />}
        marginBottom={2}
        paddingX={1}
      >
        <Button
          aria-label={t`Create Integration`}
          id="btn-save-integration"
          text={t`Create Integration`}
          onClick={handleToggleCreateModal}
          disabled={createBtnDisabledStatus}
        />
        <Button
          aria-label={t`Cancel`}
          text={t`Cancel`}
          kind="secondary"
          onClick={() => navigate('/integrations')}
        />
      </PageTitle>

      <PaddedForm>
        <Grid.Container>
          <BasicInformation
            handleForm={handleForm}
            categories={categories}
            editMode={false}
            integration={null}
          />
          <Scope
            handleForm={handleForm}
            serviceNetworkItems={serviceNetworkItems}
            integration={integration}
          />
          <AutoEnable handleForm={handleForm} />
          <Trigger
            handleForm={handleForm}
            applicationEventsItems={applicationEventsItems}
            triggerTypeHttpRequest={triggerTypeHttpRequest}
            triggerTypeApplicationEvent={triggerTypeApplicationEvent}
            integration={integration}
          />

          <Metadata
            handleForm={handleForm}
            metadataInputs={metadataInputs}
            setMetadataInputs={setMetadataInputs}
            enabledAddMetadataInput={enabledAddMetadataInput}
            setEnabledAddMetadataInput={setEnabledAddMetadataInput}
            form={form}
            setForm={setForm}
            notice={
              <ActionMetadataNotice
                metadataSchema={
                  allActions.find((a) => a.id === form.action_uuid)?.attributes
                    ?.metadata_schema
                }
              />
            }
          />
          <CacheSettings
            handleForm={handleForm}
            editMode={false}
            triggerTypeHttpRequest={triggerTypeHttpRequest}
            setCreateBtnDisabledStatus={setCreateBtnDisabledStatus}
            errors={errors}
            setErrors={setErrors}
          />
          <CircuitBreaker
            handleForm={handleForm}
            circuitBreakerEnabled={form.circuit_breaker_enabled}
            showFields={showCircuitBreakerFields}
          />
          <AutomaticRetry
            handleForm={handleForm}
            triggerTypeApplicationEvent={triggerTypeApplicationEvent}
            automaticRetryEnabled={form.automatic_retry_enabled}
            showFields={showAutomaticRetryFields}
          />
          <Commands
            handleForm={handleForm}
            commands={commands}
            commandsFlow={commandsFlow}
            categoryActions={categoryActions}
            triggerTypeHttpRequest={triggerTypeHttpRequest}
          />
        </Grid.Container>
      </PaddedForm>
    </>
  );
}
